import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getDashboardSummary, getPendingApprovals, updateBlocker } from '../api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  RefreshCw, 
  Users, 
  AlertTriangle, 
  Clock, 
  ShieldAlert,
  CheckCircle2,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  X,
  Check,
  ArrowRight
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const isManager = user?.role === 'manager';
  const isReadOnly = user?.role === 'inbound_sales';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const summaryRes = await getDashboardSummary();
      setSummary(summaryRes.data);
      
      if (isManager) {
        const approvalsRes = await getPendingApprovals();
        setApprovals(approvalsRes.data);
      }
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  }, [isManager]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleApproval = async (accountId, blockerId, action, note = null) => {
    try {
      const data = { action };
      if (note) data.rejection_note = note;
      await updateBlocker(accountId, blockerId, data);
      toast.success(action === 'approve' ? 'Blocker approved' : 'Blocker rejected');
      setRejectingId(null);
      setRejectNote('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update blocker');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full spinner" />
      </div>
    );
  }

  const teams = ['Level 1 Tech', 'Level 2 Tech', 'Inbound Sales', 'Level 3 Tech'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-text-primary">
            Dashboard
            {isReadOnly && <span className="ml-2 text-sm font-normal text-text-secondary">(Read Only)</span>}
          </h1>
          <p className="text-text-secondary text-sm mt-1">
            Pipeline overview and approvals
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 rounded-md bg-surface border border-border text-text-secondary hover:text-text-primary hover:bg-surface-highlight transition-all"
          data-testid="refresh-dashboard"
        >
          <RefreshCw size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-text-secondary mb-2">
            <Users size={18} />
            <span className="text-sm">Total Accounts</span>
          </div>
          <p className="text-3xl font-bold text-text-primary">{summary?.total_accounts || 0}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-success mb-2">
            <CheckCircle2 size={18} />
            <span className="text-sm">Completed</span>
          </div>
          <p className="text-3xl font-bold text-success">{summary?.total_completed || 0}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-destructive mb-2">
            <ShieldAlert size={18} />
            <span className="text-sm">Blocked</span>
          </div>
          <p className="text-3xl font-bold text-destructive">{summary?.total_blocked || 0}</p>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <div className="flex items-center gap-2 text-warning mb-2">
            <Clock size={18} />
            <span className="text-sm">Stale</span>
          </div>
          <p className="text-3xl font-bold text-warning">{summary?.total_stale || 0}</p>
        </div>
      </div>

      {/* Pipeline Flow */}
      <div className="bg-surface border border-border rounded-lg p-4" data-testid="pipeline-flow">
        <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">Pipeline Flow</h2>
        <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2">
          {teams.map((team, idx) => {
            const teamData = summary?.teams?.[team] || {};
            return (
              <React.Fragment key={team}>
                <div className="flex-1 min-w-[150px] p-4 bg-background rounded-lg border border-border">
                  <h3 className="text-sm font-medium text-text-secondary mb-2">{team}</h3>
                  <p className="text-2xl font-bold text-text-primary">{teamData.total || 0}</p>
                  <div className="mt-2 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-text-secondary">In Progress</span>
                      <span className="text-primary">{teamData.in_progress || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Blocked</span>
                      <span className="text-destructive">{teamData.blocked || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-secondary">Stale</span>
                      <span className="text-warning">{teamData.stale || 0}</span>
                    </div>
                  </div>
                </div>
                {idx < teams.length - 1 && (
                  <ArrowRight size={24} className="text-text-secondary flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Approval Queue - Manager Only */}
        {isManager && (
          <div className="bg-surface border border-border rounded-lg p-4" data-testid="approval-queue">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-heading font-semibold text-text-primary">
                Pending Approvals
                {approvals.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs bg-warning/10 text-warning rounded-full">
                    {approvals.length}
                  </span>
                )}
              </h2>
            </div>
            {approvals.length === 0 ? (
              <p className="text-text-secondary text-sm">No pending approvals</p>
            ) : (
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {approvals.map((approval) => (
                  <div key={`${approval.account_id}-${approval.blocker_id}`} className="p-3 bg-background rounded-md border border-border" data-testid={`approval-${approval.blocker_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono text-sm text-text-primary">{approval.account_number}</span>
                          {approval.is_client_resolution && (
                            <span className="px-1.5 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 rounded">Client</span>
                          )}
                        </div>
                        <p className="text-sm text-text-secondary">{approval.blocker_type} on {approval.device_type}</p>
                        <p className="text-xs text-text-secondary mt-1">
                          Resolved by {approval.resolved_by} • {approval.current_team}
                        </p>
                      </div>
                          <div className="flex gap-2">
                        {rejectingId === `${approval.account_id}-${approval.blocker_id}` ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={rejectNote}
                              onChange={(e) => setRejectNote(e.target.value)}
                              placeholder="Reason"
                              className="px-2 py-1 text-xs bg-surface border border-border rounded text-text-primary w-24"
                            />
                            <button
                              onClick={() => handleApproval(approval.account_id, approval.blocker_id, 'reject', rejectNote)}
                              className="p-1 text-destructive hover:bg-destructive/10 rounded"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => { setRejectingId(null); setRejectNote(''); }}
                              className="p-1 text-text-secondary hover:bg-surface-highlight rounded"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleApproval(approval.account_id, approval.blocker_id, 'approve')}
                              className="p-2 text-success hover:bg-success/10 rounded transition-colors"
                              data-testid={`approve-${approval.blocker_id}`}
                            >
                              <ThumbsUp size={16} />
                            </button>
                            <button
                              onClick={() => setRejectingId(`${approval.account_id}-${approval.blocker_id}`)}
                              className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                              data-testid={`reject-${approval.blocker_id}`}
                            >
                              <ThumbsDown size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => navigate(`/accounts/${approval.account_id}`)}
                          className="p-2 text-text-secondary hover:text-primary hover:bg-primary/10 rounded transition-colors"
                        >
                          <ExternalLink size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Escalations Panel */}
        <div className={`bg-surface border border-border rounded-lg p-4 ${!isManager && 'lg:col-span-2'}`} data-testid="escalations-panel">
          <h2 className="text-lg font-heading font-semibold text-text-primary mb-4">
            Escalations
            <span className="ml-2 text-sm font-normal text-text-secondary">(Stale Accounts)</span>
          </h2>
          
          {/* Level 3 - Red (Highest Priority) */}
          {summary?.escalations?.level_3?.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-l3-stale" />
                <span className="text-sm font-medium text-l3-stale">Level 3 - Critical</span>
              </div>
              <div className="space-y-2">
                {summary.escalations.level_3.map((acc) => (
                  <EscalationItem key={acc.id} account={acc} level={3} onClick={() => navigate(`/accounts/${acc.id}`)} />
                ))}
              </div>
            </div>
          )}
          
          {/* Level 2 - Orange */}
          {summary?.escalations?.level_2?.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-l2-stale" />
                <span className="text-sm font-medium text-l2-stale">Level 2 - Warning</span>
              </div>
              <div className="space-y-2">
                {summary.escalations.level_2.map((acc) => (
                  <EscalationItem key={acc.id} account={acc} level={2} onClick={() => navigate(`/accounts/${acc.id}`)} />
                ))}
              </div>
            </div>
          )}
          
          {/* Level 1 - Yellow */}
          {summary?.escalations?.level_1?.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-l1-stale" />
                <span className="text-sm font-medium text-l1-stale">Level 1 - Notice</span>
              </div>
              <div className="space-y-2">
                {summary.escalations.level_1.map((acc) => (
                  <EscalationItem key={acc.id} account={acc} level={1} onClick={() => navigate(`/accounts/${acc.id}`)} />
                ))}
              </div>
            </div>
          )}
          
          {!summary?.escalations?.level_1?.length && !summary?.escalations?.level_2?.length && !summary?.escalations?.level_3?.length && (
            <p className="text-text-secondary text-sm">No stale accounts</p>
          )}
        </div>
      </div>
    </div>
  );
};

const EscalationItem = ({ account, level, onClick }) => {
  const colors = {
    1: 'border-l-l1-stale hover:bg-l1-stale/5',
    2: 'border-l-l2-stale hover:bg-l2-stale/5',
    3: 'border-l-l3-stale hover:bg-l3-stale/5',
  };
  
  return (
    <div
      onClick={onClick}
      className={`p-3 bg-background rounded-md border border-border border-l-4 cursor-pointer transition-colors ${colors[level]}`}
      data-testid={`escalation-${account.id}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <span className="font-mono text-sm text-text-primary">{account.account_number}</span>
          <p className="text-sm text-text-secondary">{account.account_name}</p>
        </div>
        <div className="text-right">
          <p className="text-xs text-text-secondary">{account.current_team}</p>
          <p className="text-xs text-text-secondary">
            Last: {format(new Date(account.last_activity), 'MMM d')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
